import { ChangeEvent, FormEvent, MouseEvent, useState } from "react";

import {
  useInput,
  useArray,
  useProvidersEmployeeLayout,
  useApisMembersPrivateQuestionAnswersIndex,
  useDateRange,
} from "~/hooks";

import {
  ApiMembersPrivateQuestionAnswersIndexRequestType,
  PrivateQuestionAnswerType,
  RangeDatePropsType,
} from "~/domains";

type ReturnType = {
  privateQuestionAnswers: PrivateQuestionAnswerType[];
  keyword: string;
  startDate: Date | null;
  endDate: Date | null;
  hasNextPage?: boolean;
  isLoading: boolean;
  onChangeKeyword: (e: ChangeEvent<HTMLInputElement>) => void;
  unshiftPrivateQuestionAnswer: (newItem: PrivateQuestionAnswerType) => void;
  fetchNextPage: () => void;
  onChangeDateRange: (value: RangeDatePropsType) => void;
  onSearchSubmit: (e: FormEvent<HTMLFormElement>) => void;
  onConditionReset: (e: MouseEvent<HTMLButtonElement>) => void;
};

export const useSearchMembersPrivateQuestionAnswers = (): ReturnType => {
  const { setFalseRightSidebarMain } = useProvidersEmployeeLayout();
  const {
    items: privateQuestionAnswers,
    setItems: setPrivateQuestionAnswers,
    unshiftItem: unshiftPrivateQuestionAnswer,
  } = useArray<PrivateQuestionAnswerType>([]);
  const [
    {
      startDate,
      endDate,
      formattedStartDate,
      formattedEndDate,
      onChange: onChangeDateRange,
    },
  ] = useDateRange();

  const [{ value: keyword, onChange: onChangeKeyword }, setKeyword] =
    useInput("");

  const [params, setParams] =
    useState<ApiMembersPrivateQuestionAnswersIndexRequestType>();

  const { fetchNextPage, hasNextPage, isLoading } =
    useApisMembersPrivateQuestionAnswersIndex({
      params,
      config: {
        onSuccess: (data) => {
          setPrivateQuestionAnswers(
            data.pages.map((page) => page.privateQuestionAnswers).flat(),
          );
        },
      },
    });

  const onSearchSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setParams({
      keyword,
      postDateFrom: formattedStartDate || undefined,
      postDateTo: formattedEndDate || undefined,
    });
    setFalseRightSidebarMain();
  };

  const onConditionReset = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setKeyword("");
    setParams(undefined);
    onChangeDateRange([null, null]);
    setFalseRightSidebarMain();
  };

  return {
    privateQuestionAnswers,
    keyword,
    hasNextPage,
    startDate,
    endDate,
    isLoading,
    unshiftPrivateQuestionAnswer,
    fetchNextPage,
    onChangeKeyword,
    onChangeDateRange,
    onSearchSubmit,
    onConditionReset,
  };
};
